import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  Button,
  Progress,
  VStack,
  Stack,
  IconButton,
  HStack,
  useColorModeValue,
  Collapse,
  Center,
  Spinner,
} from '@chakra-ui/react'
import { CaretDown, CaretUp } from '@phosphor-icons/react'
import { useState, useEffect } from 'react'
import CubeAnimator from '../components/CubeAnimator'
import PLLCaseVisualizer from '../components/PLLCaseVisualizer'
import { useAuth } from '../contexts/AuthContext'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase/config'
import { PageMeta } from '../components/PageMeta'

interface Algorithm {
  id: string
  name: string
  notation: string
  description: string
  status: 'unknown' | 'learning' | 'mastered'
  category: string
}

const initialPLLAlgorithms: Algorithm[] = [
  {
    id: 'Aa',
    name: 'Aa Permutation',
    notation: "x R' U R' D2 R U' R' D2 R2 x'",
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'A'
  },
  {
    id: 'Ab',
    name: 'Ab Permutation',
    notation: "x' R U' R D2 R' U R D2 R2 x",
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'A'
  },
  {
    id: 'F',
    name: 'F Permutation',
    notation: 'R\' U\' F\' R U R\' U\' R\' F R2 U\' R\' U\' R U R\' U R',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'F'
  },
  {
    id: 'Ga',
    name: 'Ga Permutation',
    notation: 'R2 U R\' U R\' U\' R U\' R2 U\' D R\' U R D\'',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'G'
  },
  {
    id: 'Gb',
    name: 'Gb Permutation',
    notation: 'R\' U\' R U D\' R2 U R\' U R U\' R U\' R2 D',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'G'
  },
  {
    id: 'Gc',
    name: 'Gc Permutation',
    notation: 'R2 U\' R U\' R U R\' U R2 U D\' R U\' R\' D',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'G'
  },
  {
    id: 'Gd',
    name: 'Gd Permutation',
    notation: 'R U R\' U\' D R2 U\' R U\' R\' U R\' U R2 D\'',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'G'
  },
  {
    id: 'Ja',
    name: 'Ja Permutation',
    notation: 'x R2 F R F\' R U2 r\' U r U2',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'J'
  },
  {
    id: 'Jb',
    name: 'Jb Permutation',
    notation: 'R U R\' F\' R U R\' U\' R\' F R2 U\' R\'',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'J'
  },
  {
    id: 'Ra',
    name: 'Ra Permutation',
    notation: 'R U\' R\' U\' R U R D R\' U\' R D\' R\' U2 R\'',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'R'
  },
  {
    id: 'Rb',
    name: 'Rb Permutation',
    notation: 'R2 F R U R U\' R\' F\' R U2 R\' U2 R',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'R'
  },
  {
    id: 'T',
    name: 'T Permutation',
    notation: 'R U R\' U\' R\' F R2 U\' R\' U\' R U R\' F\'',
    description: 'Adjacent Corner Swap',
    status: 'unknown',
    category: 'T'
  },
  {
    id: 'E',
    name: 'E Permutation',
    notation: 'x\' L\' U L D\' L\' U\' L D L\' U\' L D\' L\' U L D',
    description: 'Diagonal Corner Swap',
    status: 'unknown',
    category: 'E'
  },
  {
    id: 'Na',
    name: 'Na Permutation',
    notation: 'R U R\' U R U R\' F\' R U R\' U\' R\' F R2 U\' R\' U2 R U\' R\'',
    description: 'Diagonal Corner Swap',
    status: 'unknown',
    category: 'N'
  },
  {
    id: 'Nb',
    name: 'Nb Permutation',
    notation: 'R\' U R U\' R\' F\' U\' F R U R\' F R\' F\' R U\' R',
    description: 'Diagonal Corner Swap',
    status: 'unknown',
    category: 'N'
  },
  {
    id: 'V',
    name: 'V Permutation',
    notation: 'R\' U R\' U\' y R\' F\' R2 U\' R\' U R\' F R F',
    description: 'Diagonal Corner Swap',
    status: 'unknown',
    category: 'V'
  },
  {
    id: 'Y',
    name: 'Y Permutation',
    notation: 'F R U\' R\' U\' R U R\' F\' R U R\' U\' R\' F R F\'',
    description: 'Diagonal Corner Swap',
    status: 'unknown',
    category: 'Y'
  },
  {
    id: 'H',
    name: 'H Permutation',
    notation: 'M2 U M2 U2 M2 U M2',
    description: 'Edges Only',
    status: 'unknown',
    category: 'H'
  },
  {
    id: 'Ua',
    name: 'Ua Permutation',
    notation: 'M2 U M U2 M\' U M2',
    description: 'Edges Only',
    status: 'unknown',
    category: 'U'
  },
  {
    id: 'Ub',
    name: 'Ub Permutation',
    notation: 'M2 U\' M U2 M\' U\' M2',
    description: 'Edges Only',
    status: 'unknown',
    category: 'U'
  },
  {
    id: 'Z',
    name: 'Z Permutation',
    notation: 'M\' U M2 U M2 U M\' U2 M2',
    description: 'Edges Only',
    status: 'unknown',
    category: 'Z'
  }
]

const PLLMethod = () => {
  const { currentUser } = useAuth()
  const [algorithms, setAlgorithms] = useState<Algorithm[]>(initialPLLAlgorithms)
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string>('')
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})
  const [showLearningOnly, setShowLearningOnly] = useState(false)
  const [loading, setLoading] = useState(true)

  // Color mode values
  const bgElevated = useColorModeValue('gray.50', 'keymash.bg.elevated')
  const bgSubtle = useColorModeValue('gray.100', 'keymash.bg.subtle')
  const textColor = useColorModeValue('gray.800', 'white')
  const borderColor = useColorModeValue('gray.200', 'keymash.border.subtle')
  const categoryColor = useColorModeValue('orange.500', 'orange.300')

  // Load saved progress when component mounts
  useEffect(() => {
    const loadProgress = async () => {
      if (!currentUser) {
        setLoading(false)
        return
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid)
        const userDoc = await getDoc(userRef)
        const userData = userDoc.data()

        if (userData?.progress?.pll?.algorithms) {
          const savedProgress = userData.progress.pll.algorithms
          const updatedAlgorithms = algorithms.map(algo => ({
            ...algo,
            status: savedProgress[algo.id] || 'unknown'
          }))
          setAlgorithms(updatedAlgorithms)
        }
      } catch (error) {
        console.error('Error loading PLL progress:', error)
      } finally {
        setLoading(false)
      }
    }

    loadProgress()
  }, [currentUser])

  const updateAlgorithmStatus = async (
    id: string,
    newStatus: Algorithm['status']
  ) => {
    if (!currentUser) return

    const updatedAlgorithms = algorithms.map(algo => {
      if (algo.id === id) {
        // If clicking the same status again, revert to unknown
        const finalStatus = algo.status === newStatus ? 'unknown' : newStatus
        return { ...algo, status: finalStatus }
      }
      return algo
    })
    setAlgorithms(updatedAlgorithms)

    // Save progress to user profile
    const userRef = doc(db, 'users', currentUser.uid)
    const groupProgress = getGroupProgress(updatedAlgorithms)
    const overallProgress = getProgress(updatedAlgorithms)

    await updateDoc(userRef, {
      'progress.pll': {
        overall: overallProgress,
        groups: groupProgress,
        lastUpdated: new Date(),
        algorithms: updatedAlgorithms.reduce((acc, algo) => ({
          ...acc,
          [algo.id]: algo.status
        }), {})
      }
    })
  }

  const getProgress = (algorithms: Algorithm[]) => {
    const mastered = algorithms.filter(algo => algo.status === 'mastered').length
    return (mastered / algorithms.length) * 100
  }

  const getGroupProgress = (algorithms: Algorithm[]) => {
    const groups = algorithms.reduce((acc, algo) => {
      if (!acc[algo.category]) {
        acc[algo.category] = { total: 0, mastered: 0 }
      }
      acc[algo.category].total++
      if (algo.status === 'mastered') {
        acc[algo.category].mastered++
      }
      return acc
    }, {} as Record<string, { total: number; mastered: number }>)

    return Object.entries(groups).reduce((acc, [category, { total, mastered }]) => ({
      ...acc,
      [category]: (mastered / total) * 100
    }), {})
  }

  const toggleGroup = (category: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Group algorithms by category
  const groupedAlgorithms = algorithms.reduce((acc, algo) => {
    if (!acc[algo.category]) {
      acc[algo.category] = []
    }
    acc[algo.category].push(algo)
    return acc
  }, {} as Record<string, Algorithm[]>)

  // Filter algorithms if showLearningOnly is true
  const filteredGroupedAlgorithms = showLearningOnly
    ? Object.entries(groupedAlgorithms).reduce((acc, [category, algos]) => {
        const filteredAlgos = algos.filter(algo => algo.status === 'learning')
        if (filteredAlgos.length > 0) {
          acc[category] = filteredAlgos
        }
        return acc
      }, {} as Record<string, Algorithm[]>)
    : groupedAlgorithms

  return (
    <Box py={8} px={4}>
      <PageMeta title="pll.pageTitle" description="pll.pageDescription" />
      <VStack spacing={6} align="stretch" maxW="container.md" mx="auto">
        <Box>
          <Heading size="xl" mb={4}>PLL (Permutation of Last Layer)</Heading>
          <Text fontSize="lg" mb={8}>
            Learn the algorithms to permute all pieces in the last layer correctly.
          </Text>
        </Box>

        {loading ? (
          <Center py={8}>
            <Spinner size="xl" color="brand.500" />
          </Center>
        ) : (
          <>
            {selectedAlgorithm && (
              <Box mb={8} display="flex" justifyContent="center">
                <CubeAnimator
                  algorithm={selectedAlgorithm}
                  showCase={true}
                  speed={500}
                  showControls={true}
                  autoPlay={false}
                  size="280px"
                />
              </Box>
            )}

            <HStack justify="space-between" align="center" mb={4}>
              <Box>
                <Text mb={2}>Overall Progress:</Text>
                <Progress value={getProgress(algorithms)} colorScheme="green" width="200px" />
              </Box>
              <Button
                size="sm"
                colorScheme="blue"
                variant={showLearningOnly ? 'solid' : 'outline'}
                onClick={() => setShowLearningOnly(!showLearningOnly)}
              >
                {showLearningOnly ? 'Show All' : 'Show Learning Only'}
              </Button>
            </HStack>

            <VStack spacing={6} align="stretch">
              {Object.entries(filteredGroupedAlgorithms).map(([category, algos]) => {
                const groupProgress = getProgress(algos)
                const isCollapsed = collapsedGroups[category]

                return (
                  <Box key={category}>
                    <Card mb={isCollapsed ? 0 : 6}>
                      <CardBody p={4}>
                        <HStack justify="space-between" align="center" width="100%">
                          <HStack spacing={4}>
                            <IconButton
                              aria-label={isCollapsed ? "Expand group" : "Collapse group"}
                              icon={isCollapsed ? <CaretDown weight="bold" /> : <CaretUp weight="bold" />}
                              variant="ghost"
                              color={categoryColor}
                              onClick={() => toggleGroup(category)}
                            />
                            <VStack align="start" spacing={1}>
                              <Heading size="md" color={categoryColor}>
                                {category}
                              </Heading>
                              <Text fontSize="sm" color="text.muted">
                                {Math.round(groupProgress)}% complete
                              </Text>
                            </VStack>
                          </HStack>
                          <Progress 
                            value={groupProgress} 
                            colorScheme="green" 
                            width="100px"
                            size="sm"
                            borderRadius="full"
                          />
                        </HStack>
                      </CardBody>
                    </Card>

                    <Collapse in={!isCollapsed} animateOpacity>
                      <VStack spacing={6} align="stretch">
                        {algos.map((algo) => (
                          <Card key={algo.id}>
                            <CardBody>
                              <VStack spacing={4} align="stretch">
                                <Heading size="md" textAlign="center">
                                  {algo.name}
                                </Heading>
                                
                                <Box>
                                  <PLLCaseVisualizer 
                                    key={`${algo.id}-${!isCollapsed}`}
                                    pllCase={algo.id} 
                                    size={{ width: "100%", height: "150px" }}
                                  />
                                </Box>

                                <Box
                                  bg={bgElevated}
                                  p={4}
                                  borderRadius="lg"
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  cursor="pointer"
                                  onClick={() => setSelectedAlgorithm(algo.notation)}
                                  _hover={{ 
                                    bg: bgSubtle,
                                    borderColor: 'keymash.border.default'
                                  }}
                                  transition="all 0.2s"
                                >
                                  <Text
                                    fontFamily="mono"
                                    fontSize={{ base: "xl", md: "2xl" }}
                                    color={textColor}
                                    textAlign="center"
                                    letterSpacing="wider"
                                    fontWeight="medium"
                                  >
                                    {algo.notation}
                                  </Text>
                                </Box>

                                <Text fontSize="sm" color="text.muted" textAlign="center">
                                  {algo.description}
                                </Text>

                                <Stack
                                  direction={{ base: 'column', sm: 'row' }}
                                  spacing={2}
                                  justify="center"
                                  mt={2}
                                >
                                  <Button
                                    size="sm"
                                    variant={algo.status === 'learning' ? 'solid' : 'outline'}
                                    colorScheme="yellow"
                                    onClick={() => updateAlgorithmStatus(algo.id, 'learning')}
                                    width={{ base: '100%', sm: 'auto' }}
                                  >
                                    Learning
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={algo.status === 'mastered' ? 'solid' : 'outline'}
                                    colorScheme="green"
                                    onClick={() => updateAlgorithmStatus(algo.id, 'mastered')}
                                    width={{ base: '100%', sm: 'auto' }}
                                  >
                                    Mastered
                                  </Button>
                                </Stack>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )
              })}
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  )
}

export default PLLMethod 